import React, { Children } from 'react';
import styled from 'styled-components';
import { BasePlugin } from '../BasePlugin/BasePlugin';
import { QueryContext, useDataTableQueryContext } from '../../utils/DataTableQueryProvider/DataTableQueryProvider';
import { QueryModel, SortModel } from '../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useFetchingState } from '../../DataTableAdvancedElements/DataTableQuickReports/utils/useFetchingState';
import { Skeleton } from '../../../Skeleton';
import { AllFiltersButton } from './builtin/AllFiltersButton';
import { ColumnConfiguration } from './builtin/ColumnConfiguration';
import { ColumnSortPicker } from './builtin/ColumnSortPicker';

interface ChildrenProps<Q extends QueryModel> {
  applyQuery: QueryContext<Q>['applyQuery'];
  query: NonNullable<QueryContext<Q>['queryVariables']>['query'];
}

interface DataTablePluginPermanentFiltersProps<Q extends QueryModel> {
  children(props: ChildrenProps<Q>): React.ReactNode;
}

export const DataTablePluginPermanentFiltersID = 'plugin-permanent-filters';

export function DataTablePluginPermanentFilters<Q extends QueryModel>({
  children
}: DataTablePluginPermanentFiltersProps<Q>) {
  const { applyQuery, queryVariables } = useDataTableQueryContext<Q, SortModel>();
  const { isPageLoading } = useFetchingState();
  const childrenCount = Children.count(children({ applyQuery, query: queryVariables?.query }));
  return (
    <BasePlugin
      id={DataTablePluginPermanentFiltersID}
      key={DataTablePluginPermanentFiltersID}
      toolbar={{
        variant: 'below',
        Component: (
          <StyledGrid key="DataTablePluginPermanentFiltersGrid">
            <StyledCell>
              {isPageLoading ? (
                Array.from(new Array(childrenCount + 1)).map((_, index) => (
                  <Skeleton
                    randomWidthAsNumber={[90, 150]}
                    key={index}
                    height={34}
                    inline
                    variant="rect"
                    style={{ borderRadius: 16 }}
                  />
                ))
              ) : (
                <>
                  {children({ applyQuery, query: queryVariables?.query })}
                  <AllFiltersButton />
                </>
              )}
            </StyledCell>
            <StyledCell $alignRight>
              {isPageLoading ? (
                <>
                  <Skeleton width={70} height={34} inline variant="rect" style={{ borderRadius: 16 }} />
                  <Skeleton width={154} height={34} inline variant="rect" style={{ borderRadius: 16 }} />
                </>
              ) : (
                <>
                  <ColumnSortPicker />
                  <ColumnConfiguration />
                </>
              )}
            </StyledCell>
          </StyledGrid>
        )
      }}
    />
  );
}

const StyledGrid = styled.div`
  display: grid;
  width: 100%;
  align-items: center;
  grid-template-columns: 1fr max-content;
`;

const StyledCell = styled.div<{ $alignRight?: boolean }>`
  & > * {
    display: inline-block;
    vertical-align: middle;
  }
  & > * + * {
    margin-left: ${({ theme }) => theme.spacingSize.m};
  }
  text-align: ${({ $alignRight }) => ($alignRight ? 'right' : undefined)};
`;
